<script setup lang="ts">
/**
 * UI-kit component
 * Category: Modals
 * Name: ModalDefault
 * FIGMA: https://www.figma.com/design/oRsJUOAucnhxx5aeqy4TRO/APP-admin?node-id=14544-95045
 */
import type { ModalDefaultProps } from '../types/interfaces'

import { faXmark, faChevronLeft } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { AppText } from '@shared/ui/typography'
import { OverlayDefault } from '@shared/ui/overlays'
import { LoaderOverlay } from '@shared/ui/loaders'
import { BtnIcon } from '@shared/ui/buttons'

import { useModalDefault } from '../composables/modal-default.hook'
import { ModalView } from '../types/interfaces'

defineOptions({
  name: 'ModalDefault',
})

withDefaults(defineProps<ModalDefaultProps>(), {
  show: false,
  title: null,
  description: null,
  loading: false,
  view: ModalView.Default,
  hidden: false,
  className: '',
  overlayClassName: '',
  isCloseButton: false,
  isBackButton: false,
})

const emit = defineEmits<{
  'click:overlay': []
  'click:close': []
  'click:back': []
}>()

const handleClickOverlay = (): void => emit('click:overlay')
const handleClickClose = (): void => emit('click:close')
const handleClickBack = (): void => emit('click:back')

const {
  refScroll,
} = useModalDefault()
</script>

<template>
  <OverlayDefault
    :show="show"
    :class="[{ hidden }, overlayClassName]"
    @click="handleClickOverlay"
  >
    <div :class="['def-modal', view, className]">
      <div class="def-modal__general">
        <BtnIcon
          v-if="isCloseButton"
          class="def-modal__close"
          view="transparent"
          size="large"
          :icon="faXmark"
          @click="handleClickClose"
        />
        <button
          v-if="isBackButton"
          class="def-modal__back"
          @click="handleClickBack"
        >
          <FontAwesomeIcon :icon="faChevronLeft" />
        </button>
        <AppText
          v-if="title"
          name="h3"
          class="def-modal__title"
        >
          {{ title }}
        </AppText>
        <AppText
          v-if="description"
          class="def-modal__description"
        >
          {{ description }}
        </AppText>
        <div
          ref="content"
          class="def-modal__content"
        >
          <slot />
        </div>
        <div
          v-if="$slots.controls"
          :class="['def-modal__controls', { scroll: refScroll }]"
        >
          <slot name="controls" />
        </div>
        <LoaderOverlay v-if="loading" />
      </div>
    </div>
  </OverlayDefault>
</template>

<style scoped lang="scss" src="../styles/_modal-default.scss" />
